import { LinearProgress } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import Breadcrumbs from '@/components/Breadcrumbs';
import {
  DatasetCodeEvaluatorCrudPane,
  DatasetEvaluatorCrudPane,
} from '@/components/EvaluatorCrudPane/DatasetEvaluatorCrudPane';
import { EvaluatorStoreProvider } from '@/components/EvaluatorCrudPane/store/EvaluatorStoreProvider';
import { useIsPromptLoading } from '@/hooks/useIsPromptLoading';
import { useRunRules } from '@/hooks/useSwr';

import { useEvaluatorStore } from '../../components/EvaluatorCrudPane/store/evaluatorStore';
import { EvaluatorDetailsPage } from './EvaluatorDetailsPage';

const EvaluatorDetails = () => {
  return (
    <EvaluatorStoreProvider storeType="detail">
      <EvaluatorDetailsInner />
    </EvaluatorStoreProvider>
  );
};

const EvaluatorDetailsInner = () => {
  const { datasetId, evaluatorId } = useParams();
  const [llmAsJudgePaneOpen, setllmAsJudgePaneOpen] = useState(false);
  const [customCodePaneOpen, setCustomCodePaneOpen] = useState(false);
  const importEvaluator = useEvaluatorStore((state) => state.importEvaluator);
  const reset = useEvaluatorStore((state) => state.reset);

  const {
    data: rules,
    isLoading,
    mutate,
  } = useRunRules(evaluatorId ? { id: [evaluatorId] } : undefined, {
    keepPreviousData: false,
  });
  const evaluator = rules?.find((r) => r.id === evaluatorId);

  useEffect(() => {
    if (rules?.[0]?.evaluators?.[0]?.structured) {
      // Data is available, import it into the store
      importEvaluator(
        rules[0].evaluators[0].structured,
        'dataset',
        null,
        rules[0].evaluator_version
      );
    }

    // Cleanup: reset store when component unmounts or evaluatorId changes
    return () => {
      reset();
    };
  }, [rules, reset, importEvaluator]);

  // If it's a code evaluator, we don't need to load the prompt
  const isLoadingPrompt =
    useIsPromptLoading('dataset-eval') && !evaluator?.code_evaluators?.length;

  const pageContent = useMemo(() => {
    if (isLoading || isLoadingPrompt) {
      return (
        <div className="flex flex-col pt-2">
          <LinearProgress />
        </div>
      );
    }
    if (!evaluator) {
      return <div className="p-6">Evaluator not found</div>;
    }
    return (
      <EvaluatorDetailsPage
        evaluator={evaluator}
        setEditModalOpen={(isOpen) => {
          if (evaluator?.evaluators?.[0]?.structured) {
            setllmAsJudgePaneOpen(isOpen);
          } else {
            setCustomCodePaneOpen(isOpen);
          }
        }}
      />
    );
  }, [isLoading, isLoadingPrompt, evaluator]);

  return (
    <div className="flex min-h-screen flex-1 flex-col bg-primary">
      <div className="bg-primary px-4 pt-3">
        <Breadcrumbs />
      </div>

      {pageContent}

      {/* Edit Evaluator Modal */}
      <EvaluatorStoreProvider storeType="crud">
        <DatasetEvaluatorCrudPane
          datasetId={datasetId!}
          evaluatorRule={evaluator}
          open={llmAsJudgePaneOpen}
          setOpen={(isOpen) => {
            if (!isOpen && llmAsJudgePaneOpen) {
              // Modal is being closed, reset store to original state
              if (evaluator?.evaluators?.[0]?.structured) {
                importEvaluator(
                  evaluator.evaluators[0].structured,
                  'dataset',
                  null,
                  evaluator.evaluator_version
                );
              }
            }
            setllmAsJudgePaneOpen(isOpen);
          }}
          onRuleSaved={() => {
            mutate(undefined); // Refresh the evaluator data
            setllmAsJudgePaneOpen(false);
          }}
          evaluatorVersion={2}
          preventReset={true}
        />
        <DatasetCodeEvaluatorCrudPane
          onSuccess={() => {
            mutate(undefined); // Refresh the evaluator data
            setCustomCodePaneOpen(false);
          }}
          datasetId={datasetId}
          rule={evaluator ?? undefined}
          open={customCodePaneOpen}
          onClose={() => {
            setCustomCodePaneOpen(false);
          }}
        />
      </EvaluatorStoreProvider>
    </div>
  );
};

export default EvaluatorDetails;
